import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";
import { TrendingUp, AlertTriangle, DollarSign, Search } from "lucide-react";

interface DeployPortfolioAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function DeployPortfolioAgentDialog({ open, onOpenChange, onSuccess }: DeployPortfolioAgentDialogProps) {
  const { orgId, familyId } = useTenant();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    agent_type: "performance",
    threshold_value: "",
    threshold_unit: "%",
    check_frequency: "daily",
    notify_email: "",
    stale_threshold_days: "90",
  });

  const agentTypes = [
    {
      value: "performance",
      label: "Performance Monitor",
      icon: TrendingUp,
      description: "Track portfolio returns and alert on significant changes"
    },
    {
      value: "exposure",
      label: "Exposure Monitor",
      icon: AlertTriangle,
      description: "Alert when single name exposure exceeds thresholds"
    },
    {
      value: "liquidity",
      label: "Liquidity Monitor",
      icon: DollarSign,
      description: "Monitor cash levels and suggest liquidation strategies"
    },
    {
      value: "stale_data",
      label: "Stale Data Detective",
      icon: Search,
      description: "Detect holdings, metrics, or documents that haven't been updated"
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: user } = await supabase.auth.getUser();
      
      const config: Record<string, unknown> = {
        threshold_value: parseFloat(formData.threshold_value) || 0,
        threshold_unit: formData.threshold_unit,
        check_frequency: formData.check_frequency,
      };
      if (formData.notify_email.trim()) {
        config.notify_email = formData.notify_email.split(",").map(e => e.trim()).filter(Boolean);
      }
      if (formData.agent_type === 'stale_data') {
        config.stale_threshold_days = parseInt(formData.stale_threshold_days) || 90;
      }

      const { error } = await supabase
        .from("portfolio_agents")
        .insert({
          org_id: orgId,
          family_id: familyId,
          name: formData.name,
          description: formData.description,
          agent_type: formData.agent_type,
          status: "active",
          config,
          created_by: user.user?.id,
        });

      if (error) throw error;

      toast.success("Portfolio agent deployed successfully");
      onOpenChange(false);
      onSuccess();
      
      // Reset form
      setFormData({
        name: "",
        description: "",
        agent_type: "performance",
        threshold_value: "",
        threshold_unit: "%",
        check_frequency: "daily",
        notify_email: "",
        stale_threshold_days: "90",
      });
    } catch (err) {
      console.error("Error deploying agent:", err);
      toast.error("Failed to deploy agent");
    } finally {
      setLoading(false);
    }
  };

  const selectedAgentType = agentTypes.find(type => type.value === formData.agent_type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Deploy Portfolio Analysis Agent</DialogTitle>
          <DialogDescription>
            Configure an automated agent to monitor your portfolio and generate recommendations
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="agent_type">Agent Type</Label>
            <Select
              value={formData.agent_type}
              onValueChange={(value) => setFormData({ ...formData, agent_type: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {agentTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <type.icon className="h-4 w-4" />
                      <div>
                        <div className="font-medium">{type.label}</div>
                        <div className="text-xs text-muted-foreground">{type.description}</div>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Agent Name</Label>
            <Input
              id="name"
              placeholder={`e.g., ${selectedAgentType?.label} - Q1 2025`}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              placeholder="Describe what this agent will monitor..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="threshold_value">
                {formData.agent_type === "performance" ? "Performance Threshold" :
                 formData.agent_type === "exposure" ? "Exposure Limit" :
                 "Minimum Cash Level"}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="threshold_value"
                  type="number"
                  step="0.01"
                  placeholder={formData.agent_type === "performance" ? "5.0" : 
                              formData.agent_type === "exposure" ? "10.0" : 
                              "1000000"}
                  value={formData.threshold_value}
                  onChange={(e) => setFormData({ ...formData, threshold_value: e.target.value })}
                  required
                />
                <Select
                  value={formData.threshold_unit}
                  onValueChange={(value) => setFormData({ ...formData, threshold_unit: value })}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="%">%</SelectItem>
                    <SelectItem value="$">$</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="check_frequency">Check Frequency</Label>
              <Select
                value={formData.check_frequency}
                onValueChange={(value) => setFormData({ ...formData, check_frequency: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {formData.agent_type === "stale_data" && (
            <div className="space-y-2">
              <Label htmlFor="stale_threshold_days">Stale Threshold (days)</Label>
              <Input
                id="stale_threshold_days"
                type="number"
                min="1"
                placeholder="90"
                value={formData.stale_threshold_days}
                onChange={(e) => setFormData({ ...formData, stale_threshold_days: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Holdings or metrics older than this many days will be flagged
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="notify_email">Notification Email (optional)</Label>
            <Input
              id="notify_email"
              type="text"
              placeholder="analyst@example.com, cio@example.com"
              value={formData.notify_email}
              onChange={(e) => setFormData({ ...formData, notify_email: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Receive email alerts for high/critical severity findings (comma-separated)
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Deploying..." : "Deploy Agent"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
